import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription, DialogHeader } from "@/components/ui/dialog";
import { HelpCircleIcon, SparklesIcon } from "lucide-react";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { SheetAgentHelpPage } from "@/constants/enums";
import HelpStepper from "./HelpStepper";
import HelpPageOverview from "./HelpPageOverview";
import HelpPageCreateRun from "./HelpPageCreateRun";
import HelpPageEditTasks from "./HelpPageEditTasks";
import HelpPageDownloading from "./HelpPageDownloading";

interface SheetAgentHelpDialogProps {
  /** Whether the dialog is open */
  open?: boolean;
  /** Callback when dialog open state changes */
  onOpenChange?: Dispatch<SetStateAction<boolean>>;
  /** Initial page to display (optional) */
  initialPage?: SheetAgentHelpPage;
}

/**
 * Main help dialog component for Sheet Agent.
 * Provides navigation between different help pages using a stepper.
 *
 * @component
 * @example
 * // Basic usage
 * <SheetAgentHelpDialog />
 *
 * @example
 * // With controlled open state and initial page
 * <SheetAgentHelpDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   initialPage={SheetAgentHelpPage.CREATE_RUN}
 * />
 */
function SheetAgentHelpDialog({
  open,
  onOpenChange,
  initialPage = SheetAgentHelpPage.OVERVIEW,
}: SheetAgentHelpDialogProps) {
  const { t } = useTranslation("modals");
  const [currentPage, setCurrentPage] = useState<SheetAgentHelpPage>(initialPage);

  const totalPages = 4; // Overview, Create Run, Edit Tasks, Download Files

  const pageLabels = [
    t("sheetagent.help.stepper.overview"),
    t("sheetagent.help.stepper.createrun"),
    t("sheetagent.help.stepper.edittasks"),
    t("sheetagent.help.stepper.downloading"),
  ];

  const handlePageChange = (page: SheetAgentHelpPage) => {
    setCurrentPage(page);
  };

  const renderHelpPage = () => {
    switch (currentPage) {
    case SheetAgentHelpPage.OVERVIEW:
      return <HelpPageOverview />;
    case SheetAgentHelpPage.CREATE_RUN:
      return <HelpPageCreateRun />;
    case SheetAgentHelpPage.EDIT_TASKS:
      return <HelpPageEditTasks />;
    case SheetAgentHelpPage.DOWNLOAD_FILES:
      return <HelpPageDownloading />;
    default:
      return <HelpPageOverview />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <HelpCircleIcon className="h-4 w-4" />
          {t("sheetagent.help.dialog.helplabel")}
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-[95vw] sm:min-w-[90vw] md:min-w-[85vw] lg:min-w-[80vw] xl:min-w-[75vw] 2xl:min-w-[70vw] h-[85vh] sm:h-[80vh] md:h-[75vh] lg:h-[70vh] xl:h-[65vh] 2xl:h-[60vh] flex flex-col p-0">
        <DialogHeader className="pt-3 px-3">
          <DialogTitle className="flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-blue-500" />
            <span>{t("sheetagent.help.dialog.title")}</span>
            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md">
              {t("sheetagent.help.dialog.betabadge")}
            </span>
          </DialogTitle>
          <DialogDescription>
            {t("sheetagent.help.dialog.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-1 min-h-0 border-t">
          {/* Vertical Stepper Sidebar */}
          <div className="w-48 border-r p-4 shrink-0">
            <HelpStepper
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              pageLabels={pageLabels}
            />
          </div>
          {/* Content Area */}
          <div className="flex-1 overflow-y-auto min-h-0 p-6">
            {renderHelpPage()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SheetAgentHelpDialog;
